/**
 * OD-89 — the router, as data.
 *
 * 🔴 WHY THIS IS ~150 LINES AND NOT A DEPENDENCY.
 *
 * ADR-034 removed `react-router-dom` for GHSA-qwww-vcr4-c8h2 and named no
 * replacement, and four units of screens have sat unreachable behind that gap
 * ever since — `App.tsx` still renders *"Components begin in §1.3"* in
 * production. The choice was never "router A or router B"; it was "take a new
 * dependency on the code path that gates every screen, or write the small thing
 * this product actually needs".
 *
 * What it actually needs is narrow enough to be worth writing: no nested routes,
 * no loaders, no code-split boundaries, no query-string state, and **no history
 * manipulation on the child's path at all** — a four-year-old's flow is a state
 * machine (`kidFlow.ts`), not a URL. What is left is: parse a hash, match it
 * against a table, and render.
 *
 * ⚠️ HASH ROUTING, NOT `history.pushState`. The frontend is a PWA served as
 * static files (§10.4 deploys it to S3 + CDN), and path routing needs every
 * unknown path rewritten to `index.html` at the edge. That rewrite is
 * infrastructure this repo cannot configure or test, and getting it wrong ships
 * a product where a refresh 404s. A hash needs nothing from the server, works
 * from a `file://` build, and survives the offline cold start §12 asks about.
 */

import type { Role } from "../../shared/types/prisma-enums";

/** Every screen the product can be at. */
export type RouteId =
  | "landing"
  | "start"
  | "connect"
  | "staff-login"
  | "school-login"
  | "operator-login"
  | "invite"
  | "school-signup"
  | "school-signup-claim"
  | "parent-phone"
  | "parent-otp"
  | "kid"
  | "sky-quest"
  | "turos-world"
  | "letter-lagoon"
  | "bridge-patterns"
  | "sky-quest-play"
  | "for-schools"
  | "teacher"
  | "parent"
  | "parent-attendance"
  | "parent-achievements"
  | "parent-announcements"
  | "parent-messages"
  | "parent-reports"
  | "school"
  | "school-reports"
  | "school-students"
  | "school-teachers"
  | "school-classes"
  | "school-games"
  | "school-subscription"
  | "platform"
  | "platform-schools"
  | "platform-children"
  | "platform-parents"
  | "platform-engagement"
  | "platform-subscriptions"
  | "platform-coupons"
  | "platform-audit"
  | "platform-broadcast"
  | "platform-requests"
  | "platform-operators"
  | "platform-system"
  | "settings-accessibility"
  | "settings-camera"
  /* ── console redesign (Figma "Kalqy", Admin page) ── */
  | "contact-sales"
  | "platform-quotes"
  | "platform-renewals"
  | "platform-groups"
  | "platform-people"
  | "platform-support"
  | "platform-games"
  | "platform-devices"
  | "platform-team"
  | "school-try-games"
  | "school-settings"
  | "school-help"
  | "teacher-play"
  | "teacher-class"
  | "teacher-children"
  | "teacher-attendance"
  | "teacher-reports"
  | "teacher-messages"
  | "teacher-help"
  /* ── console completion (MASTER-PLAN §3 F4) ── */
  | "reset-password" // #/reset/<token> — public, P3-16
  | "quote-accept" // #/quote/<token> — public, P3-10
  | "platform-website"; // #/platform/website — P3-21

export interface RouteDef {
  id: RouteId;
  /** The hash, without the `#`. */
  path: string;
  /**
   * 🔴 WHO MAY SEE IT — AND THIS IS NAVIGATION, NOT AUTHORISATION.
   *
   * Every one of these screens calls an endpoint that enforces its own scoping
   * server-side (§8.1: *"a guard cannot scope a collection"*). This list stops a
   * teacher being shown a school dashboard that would answer 403 — it is a
   * courtesy to the user, and it must never be mistaken for the control. The
   * backend has been the control since §8.1 and remains it.
   */
  roles?: readonly Role[];
  /**
   * 🔴 ROLE-GATED, BUT REACHED FROM THE SHELL'S SETTINGS CONTROL RATHER THAN
   * FROM THE TIER'S NAV LIST.
   *
   * `navFor` used to mean "every route that declares `roles`", which forced a
   * choice nobody should have to make: either a screen is in the sidebar, or it
   * is reachable by anyone who can type a hash. The parent's settings screen is
   * the case that broke it — it belongs beside Sign out like every other tier's
   * settings, and it reads one parent's own preferences, so a teacher who typed
   * `#/parent/settings` would be shown a screen whose every request 403s.
   *
   * ⚠️ IT CHANGES WHERE A LINK IS DRAWN, NEVER WHO MAY OPEN IT. `mayNavigate`
   * still reads `roles` and the server still scopes every request behind it.
   */
  chrome?: boolean;
  /** Shown as the page title and in the nav. */
  title: string;
  /**
   * Words the chrome's search matches this route on, beyond its own title.
   *
   * 🔴 THEY EXIST BECAUSE NOBODY SEARCHES WITH THE PRODUCT'S NOUNS. A parent
   * wanting to know whether their child was marked absent types "absent", not
   * "attendance"; a head teacher looking for the roster types "students". A box
   * that answered "no results" for either would teach its reader that it does
   * not work, which is the state the box was actually in — see `search.ts`.
   *
   * ⚠️ THEY MATCH BUT ARE NEVER DRAWN. The result row reads the route's `title`,
   * because a result that shows the word you typed rather than the place you are
   * going is a result you have to translate.
   *
   * ⚠️ AND THEY GRANT NOTHING. `roles` still decides what is offered and the
   * server still scopes every request behind it; a keyword on a route a reader
   * may not open is a keyword they never see, because the caller passes
   * `navFor(role)`.
   */
  keywords?: readonly string[];
}

/**
 * ⚠️ `landing` IS FIRST AND ITS PATH IS EMPTY, so an unknown hash falls back to
 * it rather than to a blank screen — see `matchRoute`.
 */
export const ROUTES: readonly RouteDef[] = [
  { id: "landing", path: "", title: "Kalqy" },

  /*
   * 🔴 THE ROLE PICKER IS A ROUTE OF ITS OWN, AND `landing` IS NOW THE WEBSITE.
   *
   * `#/` is the page a school arrives at knowing nothing about the product; the
   * three tiles a child hits are one hash further in. Splitting them is what
   * lets each screen be written for exactly one reader — the alternative is a
   * front page that explains the product to a four-year-old and shows a play
   * button to a procurement officer.
   *
   * ⚠️ IT IS A PICKER OF *ROUTES*, NOT OF ROLES. A2's rule stands: role comes
   * from the account and never from a control on screen. Choosing "Teacher"
   * here opens the staff sign-in form — it does not assert anything the server
   * would then have to disprove.
   */
  { id: "start", path: "start", title: "Sign in" },

  /*
   * 🔴 PUBLIC, AND IT IS A CONVERSATION RATHER THAN A SIGN-UP.
   *
   * `admin-panel.md` §3: *"a school cannot mint its own first admin"* — which is
   * why `provisionSchoolAdmin` is operator-only and why there is no
   * self-registration here. The screen asks a school for its details and posts
   * them to `POST /demo-requests`, which creates a row an operator reads on F6
   * and nothing else — no School, no User, no Subscription. The published price
   * list sits under the form, showing only plans an operator marked public, so a
   * bespoke district rate never appears on it.
   *
   * ✅ IT WAS `plans` / `#/plans`, AND BOTH HALVES OF THE RENAME ARE THE SAME
   * DECISION. The page's job changed — a price list a school reads became a form
   * a school fills in — so the nav label became "Connect", and a path that still
   * said `plans` would be the one part of the product still calling it the old
   * thing. `routes.test.ts` round-trips every route through its own path, so a
   * half-finished rename cannot ship quietly.
   */
  { id: "connect", path: "connect", title: "Connect" },
  /* P1–P2 — the public enquiry form. Also reachable as the plain path
     /contact-sales, which main.tsx rewrites to this hash route. */
  { id: "contact-sales", path: "contact-sales", title: "Contact sales", keywords: ["enquiry", "demo", "pricing", "school"] },

  /*
   * 🔴 ONE DOOR PER TIER, AND EVERY ONE OF THEM SAYS WHOSE IT IS.
   *
   * ✅ THIS WAS `signin`, AND THE BARE WORD WAS THE LAST TRACE OF THE FORM THAT
   * SERVED EVERYONE. Once the doors were split, three of the four were named for
   * the tier they open — `school/signin`, `parent/signin`, `operator` — and the
   * teacher's kept the generic address purely because it already existed. That
   * left the product with a URL that reads like a way in for anybody and is in
   * fact the teacher form, sitting beside `start`, whose title is literally
   * "Sign in" and which IS the door picker. Somebody typing the obvious address
   * to choose a door got a teacher's password field instead.
   *
   * A2 was one form for every staff role, and the rule it rested on still
   * stands and is untouched: *role comes from the account, never from a control
   * on screen*. None of these three routes asserts anything to the server —
   * they all POST the same `/auth/login`, which still answers one generic 401.
   * What a door decides is which screen a person arrives at and, once the whole
   * sign-in has SUCCEEDED, whether this was the right address for the account
   * that turned up.
   *
   * ⚠️ NO ALIAS WAS LEFT BEHIND AT `signin`, DELIBERATELY. A redirect row would
   * be the same duplicate address under a different name — and `matchRoute`
   * already sends an unknown hash to the landing page rather than to a 404,
   * precisely so a stale bookmark lands somewhere useful. A teacher who had
   * bookmarked the old address gets the front page with a "Teacher · Sign in"
   * tile on it, which is one tap from where they were going.
   *
   * ⚠️ THE WRONG-DOOR CHECK IS STRICTLY AFTER AUTHENTICATION — see
   * `finishSignInAtDoor` in `AppRouter.tsx`. Refusing earlier would tell an
   * unauthenticated caller which addresses are operator accounts, which is the
   * enumeration oracle A2 exists to avoid. Only somebody already holding the
   * credential can read the answer, so it describes nothing they did not know.
   *
   * ⚠️ NONE OF THE THREE CARRIES `roles`, AND THAT IS NOT AN OVERSIGHT. They are
   * sign-in screens; a role list would make the page that CREATES a session
   * reachable only by somebody who already had one. It also keeps them out of
   * `navFor`, which returns only routes that carry `roles` — so no door ever
   * appears in a sidebar.
   */
  /*
   * ⚠️ `teacher/signin` SHARES A PREFIX WITH THE GUARDED TEACHER TIER, exactly
   * as `school/signin` does with the school dashboard, and it is safe for the
   * identical reason: `matchRoute` is an EXACT-MATCH table. `teacher` and
   * `teacher/signin` are two rows compared with `===`, and the router's sole
   * prefix rule is `invite/`. A future prefix rule for `teacher/` would make
   * this public route reachable-by-inheritance from a `roles`-gated one and
   * must not be written. `routes.test.ts` asserts both halves.
   */
  { id: "staff-login", path: "teacher/signin", title: "Teacher sign-in" },

  /*
   * ⚠️ `school/signin` SHARES A PREFIX WITH THE `@Roles('ADMIN')` DASHBOARD
   * ROUTES, AND IS THE ONE PUBLIC SCREEN THAT DOES. The objection recorded at
   * `operator-login` below — that a public screen under a guarded prefix is an
   * exception a reader carries at every glance — applies here and was accepted
   * deliberately, because the address is one a school admin is told once and
   * bookmarks.
   *
   * 🔴 IT IS SAFE ONLY BECAUSE `matchRoute` IS AN EXACT-MATCH TABLE. `school`
   * and `school/signin` are two rows compared with `===`; the sole prefix match
   * in the router is `invite/`. A future prefix rule for `school/` would make
   * this route reachable-by-inheritance and must not be written.
   */
  { id: "school-login", path: "school/signin", title: "School admin sign-in" },

  /*
   * 🔴 THE OPERATOR'S OWN FRONT DOOR — A SECOND SIGN-IN SCREEN, DELIBERATELY.
   *
   * A2 is one form for teachers, school admins and operators, and its rule
   * stands: *role comes from the account, never from a control on screen*. This
   * route does not break it — it POSTs to the same `/auth/login` and asserts
   * nothing about who is typing. What it changes is which screen a person
   * arrives at, and that is a product decision rather than a security one:
   *
   *   · The staff form is reached from A1, a page whose largest element is a
   *     play tile for a four-year-old. An operator opening the platform tier
   *     forty times a week does not belong in that flow, and every sentence on
   *     A2 ("Your school admin sends an invitation") is written for somebody
   *     else.
   *   · The operator tier is deployed on its own hostname (`shared/lib/host.ts`),
   *     so a school that bookmarks the product never learns this door exists.
   *
   * ⚠️ NO `roles`, AND THAT IS NOT AN OVERSIGHT. It is a sign-in screen. A role
   * list here would make the page reachable only by somebody already holding the
   * session it exists to create.
   *
   * ⚠️ THE PATH IS `operator`, NOT `platform/signin`. Everything under
   * `platform/` is `@Roles('SUPER_ADMIN')` on the server and gated by
   * `mayNavigate` in the browser; a public screen sharing that prefix is the one
   * exception a reader would have to hold in their head at every glance.
   */
  { id: "operator-login", path: "operator", title: "Operator sign-in" },
  /*
   * 🔴 THE ONLY ROUTE IN THIS TABLE THAT CARRIES A VALUE IN THE URL, AND THE ONE
   * PLACE WHERE THAT IS UNAMBIGUOUSLY RIGHT.
   *
   * The comment at the bottom of this file refuses route parameters in general,
   * because *"an id in a URL is a thing a user can edit, so every parameterised
   * route needs its own scoping story"*. An invitation's scoping story is
   * already written and is the strongest in the product: the token IS the
   * authentication — 256 bits, single-use, 72 hours — and `claim` answers one
   * generic refusal for unknown, expired, revoked and already-claimed alike. An
   * editable token in a URL is precisely what an invitation link is; editing it
   * produces the same refusal as inventing one.
   *
   * ⚠️ IT IS A PREFIX MATCH, HANDLED IN `matchRoute`, NOT A PARAMETER SYSTEM.
   * One special case that says so beats a general mechanism nothing else needs.
   */
  { id: "invite", path: "invite", title: "Set your password" },

  /*
   * 🔴 A SCHOOL ASKING TO EXIST, AND `school/signup` IS AN EXACT MATCH LIKE
   * `school/signin`. It shares the `school/` prefix with the `@Roles('ADMIN')`
   * dashboard for the same reason and with the same protection: `matchRoute` is
   * an exact-match table, and the sole prefix rule in this router is `invite/`.
   *
   * ⚠️ §3 SAYS *"A SCHOOL CANNOT MINT ITS OWN FIRST ADMIN"*, AND THIS SCREEN
   * DOES NOT. It collects a form and asks the server for a link; nothing is
   * created until that link comes back. The operator's provisioning path is
   * untouched and remains how a school is onboarded by hand.
   */
  {
    id: "school-signup",
    path: "school/signup",
    title: "Create a school account",
  },

  /*
   * 🔴 THE CLAIM LINK, AND ITS PATH IS DELIBERATELY *NOT* UNDER `school/`.
   *
   * It carries a token, so it needs a prefix match — and the note on
   * `school-login` above says in as many words that a prefix rule for `school/`
   * *"would make this public route reachable-by-inheritance from a `roles`-gated
   * one and must not be written"*. `signup/` is a fresh top-level segment with
   * nothing guarded under it, so the second prefix rule this router has ever
   * needed cannot reach a dashboard.
   *
   * ⚠️ THE TOKEN IS THE AUTHENTICATION, exactly as an invitation's is: 256 bits,
   * single-use, dead in 72 hours, and editing it produces the same refusal as
   * inventing one.
   */
  { id: "school-signup-claim", path: "signup", title: "Create your school" },

  /*
   * 🔴 TWO MORE CREDENTIAL LINKS, SAME SHAPE AS `invite/` AND `signup/`.
   *
   * A password-reset link and a quote-acceptance link each carry a single-use
   * token that IS the authentication, so each needs a prefix match — and each
   * is a fresh top-level segment with nothing guarded under it, so neither can
   * make a `roles`-gated screen reachable by inheritance. The server answers
   * one generic refusal for an unknown, expired or used token, exactly as
   * `claim` does; the client only extracts it (`resetTokenFrom`,
   * `quoteTokenFrom`).
   */
  { id: "reset-password", path: "reset", title: "Reset your password" },
  { id: "quote-accept", path: "quote", title: "Your Kalqy quote" },
  { id: "parent-phone", path: "parent/signin", title: "Parent sign-in" },
  { id: "parent-otp", path: "parent/code", title: "Enter your code" },

  /*
   * 🔴 THE CHILD'S ROUTE HAS NO ROLE LIST AND TAKES NO PARAMETERS.
   *
   * A device token names exactly one player (§4.1), so there is nothing for a
   * URL to select and nothing a child could type. Putting a `playerId` in the
   * hash would create a way to ask for a different child that the token would
   * then have to refuse — a refusal that should never need to exist.
   */
  { id: "kid", path: "play", title: "Play" },

  /*
   * 🔴 THE FOUR GAME PAGES — PUBLIC WEBSITE PAGES, ONE PER GAME, AT THE GAME'S
   * OWN NAME. The route id IS the `GameId`, which is what lets the website build
   * a game's link from the game (`pathFor(game.id)`) with no second table to
   * drift. They are the documented default order (`games-spec.md` §2); Kalqy
   * World has no page and its card leads to Connect.
   *
   * ⚠️ NO `roles`, AND NOT ENTRY ROUTES — the same reasoning as the free round
   * below: a signed-in teacher showing a parent what a game teaches must not be
   * bounced to their dashboard for asking.
   */
  { id: "sky-quest", path: "sky-quest", title: "Sky Quest" },
  { id: "turos-world", path: "turos-world", title: "Turo’s World" },
  /*
   * The website's page for the school buyer — what a school gets, how it is set
   * up, what teachers and parents see. Public, no `roles`, not an entry route,
   * for the same reasons as the game pages.
   *
   * ⚠️ THE PATH AND THE HOME PAGE'S SECTION ID ARE DIFFERENT THINGS, which is
   * why the section is `who-its-for` and not `for-schools`: `#/for-schools` is a
   * route, `#who-its-for` an anchor, and the two used to be one word apart.
   */
  { id: "for-schools", path: "for-schools", title: "Kalqy for schools" },
  { id: "letter-lagoon", path: "letter-lagoon", title: "Letter Lagoon" },
  { id: "bridge-patterns", path: "bridge-patterns", title: "Bridge Patterns" },

  /*
   * 🔴 THE FREE GAME, AND ITS PATH IS DELIBERATELY *NOT* UNDER `play/`.
   *
   * `play` is the child's signed-in library: `KidSignInScreen` stands in front
   * of it and every round it runs is written against a roster Player. This is a
   * public sample of ONE game — Sky Quest, opened by anybody from the website,
   * with no account, no session and nothing recorded (see `FreePlayApp`).
   * Nesting it under `play/` would put a screen that stores nothing inside the
   * address that stores everything.
   *
   * ✅ IT WAS `sky-quest`, AND THE GAME'S PAGE TOOK THAT ADDRESS. `#/sky-quest`
   * now explains the game and offers "Play free now", which comes here — so an
   * old bookmark to the free round lands one tap from it, on a page that says
   * what it is, rather than dropping a stranger straight into a camera prompt.
   *
   * ⚠️ NO `roles`, AND IT IS NOT AN ENTRY ROUTE. It is not a door, it creates no
   * session and it reads none, and a signed-in teacher who wants to show a parent
   * the free game must not be bounced to their dashboard for asking. `navFor`
   * returns only routes that carry `roles`, so it is out of every sidebar too.
   */
  { id: "sky-quest-play", path: "sky-quest/play", title: "Sky Quest" },

  {
    id: "teacher",
    path: "teacher",
    roles: ["TEACHER", "COORDINATOR"],
    title: "My classes",
    keywords: ["class", "classes", "roster", "register", "attendance", "play", "lesson"],
  },
  { id: "teacher-play", path: "teacher/play", roles: ["TEACHER", "COORDINATOR"], title: "Start play", keywords: ["play","session","child","start"] },
  { id: "teacher-class", path: "teacher/class", roles: ["TEACHER", "COORDINATOR"], title: "Class dashboard", keywords: ["class","dashboard","overview","skills"] },
  { id: "teacher-children", path: "teacher/children", roles: ["TEACHER", "COORDINATOR"], title: "Children", keywords: ["children","students","roster","profile"] },
  { id: "teacher-attendance", path: "teacher/attendance", roles: ["TEACHER", "COORDINATOR"], title: "Attendance", keywords: ["attendance","register","absent","present"] },
  { id: "teacher-reports", path: "teacher/reports", roles: ["TEACHER", "COORDINATOR"], title: "Reports", keywords: ["reports","progress","analytics","card"] },
  { id: "teacher-messages", path: "teacher/messages", roles: ["TEACHER", "COORDINATOR"], title: "Messages", keywords: ["messages","parents","chat"] },
  { id: "teacher-help", path: "teacher/help", roles: ["TEACHER", "COORDINATOR"], title: "Help & support", keywords: ["help","support","problem","ticket","issue"] },

  /*
   * 🔴 THE PARENT TIER IS SIX ROUTES, AND IT WAS ONE.
   *
   * C2–C5 and the consent screen were built, tested and mounted nowhere:
   * `ParentTier` rendered C1 with `onOpenDetail` and `onOpenConsent` as empty
   * callbacks, so the buttons on a parent's dashboard did nothing at all. The
   * comment there called it *"a route away"* and it was right — `DashboardShell`
   * builds its nav from `navFor(role)`, which can only produce links to ROUTES,
   * so a tier with one route gets a navigation bar with one item pointing at the
   * page already open. That is what a parent saw.
   *
   * ⚠️ THE ACTIVE CHILD IS NOT IN THE URL, and that is the rule at the bottom of
   * this file rather than an omission. `parentFlow` holds it, `ParentTier` stays
   * mounted across all six, and the reducer still refuses an id that is not one
   * of this parent's own — a `playerId` in the path would be a value a parent
   * could edit, and then every one of these routes needs its own scoping story.
   */
  { id: "parent", path: "parent", roles: ["PARENT"], title: "Overview", keywords: ["home", "dashboard", "summary", "week"] },
  {
    id: "parent-attendance",
    path: "parent/attendance",
    roles: ["PARENT"],
    title: "Attendance",
    keywords: ["absent", "absence", "present", "register", "calendar", "days", "holiday"],
  },
  /*
   * ⚠️ THERE IS NO `parent/homework` AND NO `parent/detail`, THOUGH BOTH ARE
   * SCREENS. Homework already renders inside the Progress page, where a parent
   * reading their child's week is already looking; a nav item pointing at a
   * panel one scroll further down is a second door to the same room. C2's
   * progress detail is a LEAF — opened from the button on C1, closed by BACK —
   * which is the hub-and-four-leaves shape `parentFlow` was written for.
   *
   * The budget is the reason both are stated rather than assumed: `navFor`
   * returns every route carrying `roles`, so an extra route here is an extra
   * item on a bar drawn across the bottom of a 360 px phone.
   */
  {
    id: "parent-achievements",
    path: "parent/achievements",
    roles: ["PARENT"],
    title: "Achievements",
    keywords: ["awards", "stickers", "coins", "rewards", "badges", "streak"],
  },
  /*
   * ✅ THE SIXTH ITEM, AND THE BUDGET NOTE ABOVE IS WHY IT IS THE LAST ONE.
   *
   * 🔴 IT IS NOT "MESSAGES" AND MUST NOT BE FOLDED INTO IT. C4 is the record of
   * what WE sent a parent — a feed of template names and delivery states, tagged
   * by channel — and this is what their SCHOOL said. A family looking for "when
   * does the bus leave on Friday" would be reading a list of `weekly_report`
   * rows, and the notice would be one item in it with a delivery status beside
   * it. Two different authors, two different questions.
   *
   * ⚠️ ACCOUNT-WIDE, LIKE C4 AND UNLIKE EVERY OTHER ITEM IN THIS TIER. An
   * announcement is a fact about a school and every family gets the same one, so
   * `parentFlow` does not gate it on an active child — a parent with nothing
   * linked yet can still read what their school has said.
   */
  {
    id: "parent-announcements",
    path: "parent/announcements",
    roles: ["PARENT"],
    title: "Announcements",
    keywords: ["notices", "notice", "school", "news", "broadcast"],
  },
  {
    id: "parent-messages",
    path: "parent/messages",
    roles: ["PARENT"],
    title: "Messages",
    keywords: ["teacher", "chat", "inbox", "reply", "contact"],
  },
  /*
   * ⚠️ THE SEVENTH ITEM, AND IT SPENDS A BUDGET THE NOTES ABOVE SAY IS ALREADY
   * SPENT. Requested deliberately: *"in parent section add report section in
   * side bar"*. The cost is stated rather than hidden — on a 360 px phone this
   * tier's nav is the thumb bar across the bottom (`nav="adaptive"`), and seven
   * items is one more than the six that were sized to fit. On a sidebar from
   * `md` up there is room to spare; the phone is the constraint, so if the bar
   * crowds, this is the item that arrived last. 📋 Recorded.
   *
   * 🔴 IT IS A DOOR TO C2, NOT A NEW SCREEN. The note above says C2's progress
   * detail is a LEAF opened from C1 and closed by BACK — that is still how it
   * behaves; this adds a second way in for a parent who came looking for "the
   * report" rather than for their child's week. `SCREEN_FOR_ROUTE` maps it to
   * the same `C2`, so there is one screen and one place its behaviour lives.
   */
  {
    id: "parent-reports",
    path: "parent/reports",
    roles: ["PARENT"],
    title: "Reports",
    keywords: ["progress", "skills", "levels", "print", "pdf", "detail", "learning"],
  },
  /*
   * ✅ THE PARENT TIER HAS NO SETTINGS SCREEN AND NO SETTINGS CONTROL (ADR-051).
   *
   * 🔴 IT HAD BOTH, AND THAT WAS THE PROBLEM THIS FINALLY CLOSES. A parent was
   * offered the word twice: `parent-settings` behind the shell's control, and
   * this tier's preferences under an invented nav name. The first fix pointed
   * both at one screen; the second — this one — removed the screen, because a
   * tier that authenticates with a phone number and a one-time code has no
   * account settings that are not already privacy settings.
   *
   * ⚠️ THE CONTROLS DID NOT GO WITH IT. Messaging language, quiet hours, the
   * reduce-motion switch and the PER-CHILD RECORDING SWITCH are all drawn on
   * the parent home's Privacy & consent view — `#/parent?view=privacy`, a
   * sub-view of `parent`, NOT a route of its own (there is no `parent-privacy`
   * in this table). I5 makes the parent one layer of a chain that "any layer
   * may restrict", so removing the screen while keeping the control was the
   * only shape this could take: a right nobody can reach is a right on paper.
   */
  {
    id: "school",
    path: "school",
    roles: ["ADMIN", "PRINCIPAL"],
    title: "School",
    keywords: ["dashboard", "overview", "home"],
  },
  {
    id: "school-reports",
    path: "school/reports",
    roles: ["ADMIN", "PRINCIPAL"],
    title: "Reports",
    keywords: ["coverage", "export", "print", "pdf", "insights"],
  },
  /*
   * 🔴 E7 — EVERY CHILD IN THE SCHOOL, AND THE READ THAT HAD NO SCREEN AT THIS
   * TIER.
   *
   * `GET /students` admits ADMIN and PRINCIPAL and `ScopeService.classScope`
   * answers `{ schoolId }` for both, so a head teacher has been able to read
   * every child in their school since §8.1 — from a route that only existed
   * inside `TeacherApp`. E1 could tell them how many children there were and not
   * one of their names, and E5 aggregates the whole school into coverage. The
   * question neither answers is *"how is this particular child getting on"*,
   * which is what a parent asks a principal in the corridor.
   *
   * ⚠️ IT SITS ABOVE TEACHERS AND CLASSES BECAUSE IT IS THE PEOPLE THE SCHOOL IS
   * FOR. Reports stays directly under the dashboard: it is the artefact an
   * inspector is shown, and this is the daily reading surface beneath it.
   *
   * ⚠️ A PRINCIPAL READS IT, LIKE E3 AND E4 — and unlike those two there is no
   * `canWrite` to pass, because the screen draws no write control for anybody.
   * A child's class, roll and band are E4's; the teacher's four mid-term
   * controls are D5's. See `SchoolStudentReport`.
   */
  {
    id: "school-students",
    path: "school/students",
    roles: ["ADMIN", "PRINCIPAL"],
    title: "Children",
    keywords: ["students", "pupils", "child", "roster", "names"],
  },
  /*
   * 🔴 THE ROW-LEVEL READ BEHIND E1's GAME CARDS, AND THE FIRST SCREEN IN THIS
   * TIER WHERE A NAME, A GAME AND A TIMESTAMP APPEAR ON ONE LINE.
   *
   * E1 folds the school to one figure per game — *"23 of 33 children have
   * played Kalqy World"* — which answers whether a game is being used and
   * cannot answer the question a parent actually asks: *"has MY child played,
   * and when"*. Until this route the only way to that was to open one child at
   * a time from E7.
   *
   * ⚠️ IT SITS DIRECTLY AFTER "Children", NOT AT THE FOOT OF THE TIER.
   * `navFor` returns this table in order, and a log of who played what belongs
   * beside the roster of who they are rather than below the billing page.
   *
   * ⚠️ `ADMIN` AND `PRINCIPAL` ALIKE, WHICH IS THE WHOLE TIER'S RULE. It is a
   * read with no control on it to be denied — S6 makes a principal school
   * read-only and there is nothing here to write.
   */
  {
    id: "school-games",
    path: "school/games",
    roles: ["ADMIN", "PRINCIPAL"],
    title: "Games",
    keywords: ["game", "play", "log", "sessions", "history", "score", "who played"],
  },
  /*
   * 🔴 E3/E4 — THE SCREEN THAT MAKES A SCHOOL'S TEACHERS EXIST.
   *
   * `wireframes.md` lists it (*"E1 dashboard · E3 teachers · E4 classes"*) and
   * the tier shipped without it, which left `Class.teacherId` writable by
   * `PATCH /classes/:classId` and unreachable by anything — an admin had no way
   * to see a teacher, create one, or discover an id to assign.
   *
   * ⚠️ PRINCIPAL READS IT, LIKE THE DASHBOARD AND REPORTS. *"Who teaches here"*
   * is exactly what a principal logs in for. Every write on it is ADMIN-only in
   * the service, and the screen draws no control a principal could press — D4's
   * rule rather than a disabled button.
   */
  {
    id: "school-teachers",
    path: "school/teachers",
    roles: ["ADMIN", "PRINCIPAL"],
    title: "Teachers",
    keywords: ["staff", "teacher", "invite"],
  },
  /*
   * 🔴 E4 — THE SCREEN THAT MAKES A CLASS EXIST, AND THE ONE PLACE ITS SETUP IS
   * ASSEMBLED.
   *
   * `wireframes.md` lists it beside E3 (*"E3 teachers · E4 classes"*) and the
   * tier shipped without it. Everything a class IS was writable by an endpoint
   * and reachable from no screen: `POST /classes` created one, `PATCH
   * /classes/:id { teacherId }` named its teacher, and `ScheduleService.upsert`
   * filled its week. An admin's only route to any of it was the teachers screen,
   * which answers *"who works here"* rather than *"what is Class 2B"*.
   *
   * 🔴 IT IS ONE SCREEN BECAUSE A CLASS IS ONE DECISION. Its teacher, its
   * subjects, who teaches each of them, and when they meet are four answers to
   * *"how does this class run"* — split across four addresses, an admin setting
   * up a school in September navigates between them for every class they own.
   *
   * ⚠️ PRINCIPAL READS IT, LIKE THE DASHBOARD AND THE TEACHER LIST. S6 makes a
   * principal school read-only and every write below is ADMIN-only in the
   * service; the screen draws no control they could press — D4's rule rather
   * than a disabled button.
   */
  {
    id: "school-classes",
    path: "school/classes",
    roles: ["ADMIN", "PRINCIPAL"],
    title: "Classes",
    keywords: ["sections", "class", "groups", "rooms"],
  },
  /*
   * ⚠️ SETTINGS AND ROLLOVER ARE ADMIN-ONLY, WHERE THE OTHER TWO ARE NOT.
   * S6 makes a principal *"school read-only"*, and both of these are writes —
   * the settings screen carries the recording default, which is a privacy
   * control. The server refuses a principal either way; this stops them being
   * shown a form that cannot save.
   */
  /*
   * ⚠️ A READ, AND THE ONLY PART OF F3 A SCHOOL SEES. *"What are we paying and
   * until when"* is a fair question for the admin who signed the contract; every
   * write on it is operator-only, because a school changing its own plan is the
   * discount-waiting-to-happen §4 already rules out for self-reported seats.
   */
  {
    id: "school-subscription",
    path: "school/subscription",
    roles: ["ADMIN", "PRINCIPAL"],
    title: "Billing",
    keywords: ["plan", "billing", "invoice", "payment", "seats", "renewal"],
  },
  { id: "school-try-games", path: "school/try-games", roles: ["ADMIN", "PRINCIPAL"], title: "Try games", keywords: ["games","demo","trial","try"] },
  { id: "school-settings", path: "school/settings", roles: ["ADMIN", "PRINCIPAL"], title: "Settings", keywords: ["settings","profile","school","preferences"] },
  { id: "school-help", path: "school/help", roles: ["ADMIN", "PRINCIPAL"], title: "Help & support", keywords: ["help","support","ticket","issue","problem"] },

  /*
   * 🔴 THE PLATFORM TIER IS FIVE ROUTES, NOT ONE — AND THE MISSING SIXTH IS THE
   * DECISION WORTH READING.
   *
   * `information-architecture.md` §1 maps F1–F5 to five paths under `/admin`,
   * and §2 gives SuperAdmin *"sidebar (6 items)"*. Four of the five are here
   * plus a system page, and **F3 (subscriptions / billing) is deliberately
   * absent**: `platform-metrics.ts` refuses it in writing — OD-4 (pricing) is
   * open, there is no payment provider in the repo, and `org.prisma` calls a
   * billing column *"a speculative column is a migration you live with"*. A
   * route to a screen that could only show an invented number is a nav item that
   * teaches an operator the product has a feature it does not.
   *
   * ⚠️ THE PATHS ARE `platform/*`, NOT `admin/*`, AND THAT IS ON PURPOSE. The
   * IA's `/admin` predates the school tier's own `/school` routes, and "admin"
   * is what a *school* admin is called everywhere else in this product (`A2`'s
   * copy: *"School admin"*). Two meanings of the word in one URL space is how a
   * support ticket about "the admin page" costs a round trip to disambiguate.
   * The tier's own vocabulary — platform — is unambiguous, and it already names
   * the backend's controller. 📋 Recorded rather than assumed.
   */
  {
    id: "platform",
    path: "platform",
    roles: ["SUPER_ADMIN"],
    title: "Dashboard",
    keywords: ["overview", "home", "metrics"],
  },
  {
    id: "platform-schools",
    path: "platform/schools",
    roles: ["SUPER_ADMIN"],
    title: "Schools",
    keywords: ["customers", "accounts", "tenants", "onboard"],
  },
  {
    id: "platform-requests",
    path: "platform/requests",
    roles: ["SUPER_ADMIN"],
    title: "Pipeline",
    keywords: ["demo", "leads", "queue", "signup", "enquiries", "requests", "pipeline"],
  },
  {
    id: "platform-children",
    path: "platform/children",
    roles: ["SUPER_ADMIN"],
    title: "Users",
    keywords: ["children", "pupils", "students", "players", "kids"],
  },
  {
    id: "platform-parents",
    path: "platform/parents",
    roles: ["SUPER_ADMIN"],
    title: "Parents",
    keywords: ["families", "guardians", "accounts"],
  },
  {
    id: "platform-engagement",
    path: "platform/engagement",
    roles: ["SUPER_ADMIN"],
    title: "Engagement",
    keywords: ["usage", "activity", "retention", "sessions", "analytics"],
  },
  {
    id: "platform-subscriptions",
    path: "platform/subscriptions",
    roles: ["SUPER_ADMIN"],
    title: "Subscriptions",
    keywords: ["billing", "plans", "revenue", "invoices", "payments"],
  },
  {
    id: "platform-coupons",
    path: "platform/coupons",
    roles: ["SUPER_ADMIN"],
    title: "Coupons",
    keywords: ["discounts", "promo", "codes", "offers"],
  },
  {
    id: "platform-audit",
    path: "platform/audit",
    roles: ["SUPER_ADMIN"],
    title: "Audit log",
    keywords: ["log", "history", "trail", "events", "who"],
  },
  {
    id: "platform-broadcast",
    path: "platform/broadcast",
    roles: ["SUPER_ADMIN"],
    title: "Broadcast",
    keywords: ["announce", "message", "notify", "email"],
  },
  {
    id: "platform-operators",
    path: "platform/operators",
    roles: ["SUPER_ADMIN"],
    title: "Operators",
    keywords: ["admins", "staff", "team", "access"],
  },
  {
    id: "platform-system",
    path: "platform/system",
    roles: ["SUPER_ADMIN"],
    title: "System",
    keywords: ["health", "status", "config", "flags", "jobs"],
  },
  { id: "platform-quotes", path: "platform/quotes", roles: ["SUPER_ADMIN"], title: "Quotes", keywords: ["quote","pricing","proposal","offer"] },
  { id: "platform-renewals", path: "platform/renewals", roles: ["SUPER_ADMIN"], title: "Renewals", keywords: ["renewal","expiring","subscription","churn"] },
  { id: "platform-groups", path: "platform/groups", roles: ["SUPER_ADMIN"], title: "School groups", keywords: ["group","trust","chain","franchise"] },
  { id: "platform-people", path: "platform/people", roles: ["SUPER_ADMIN"], title: "People", keywords: ["people","users","children","teachers","parents"] },
  { id: "platform-support", path: "platform/support", roles: ["SUPER_ADMIN"], title: "Support", keywords: ["support","tickets","issues","help","inbox"] },
  { id: "platform-games", path: "platform/games", roles: ["SUPER_ADMIN"], title: "Games", keywords: ["games","content","releases"] },
  { id: "platform-devices", path: "platform/devices", roles: ["SUPER_ADMIN"], title: "Devices", keywords: ["devices","health","camera","fps","system"] },
  { id: "platform-team", path: "platform/team", roles: ["SUPER_ADMIN"], title: "Team", keywords: ["team","operators","roles","access","rbac"] },
  {
    id: "platform-website",
    path: "platform/website",
    roles: ["SUPER_ADMIN"],
    title: "Website",
    keywords: ["website", "marketing", "game pages", "for schools", "enquiry form", "content"],
  },

  /*
   * 🔴 G1–G4 — THE FOUR SCREENS `information-architecture.md` §1 ROUTES AND
   * NOTHING BUILT. `useReduceMotion.ts` says its in-app switch is *"surfaced on
   * G3 Accessibility (Unit 10)"*; that screen did not exist, so the setting was
   * applied everywhere and settable nowhere. G1 is worse: until
   * `POST /auth/password` there was no way for anybody to rotate a staff
   * credential at all.
   *
   * ⚠️ NO `roles`, AND THAT IS NOT AN OVERSIGHT. These are every signed-in
   * adult's own settings — a teacher, an admin and an operator all change their
   * own password and their own language. A role list would be a claim that some
   * tier owns them.
   *
   * 🔴 SO THEY ARE ABSENT FROM `navFor`, WHICH READS `roles !== undefined`.
   * Deliberate: they are reached from a Settings control in the shell chrome,
   * beside sign-out, rather than competing with the screens a teacher opens
   * during a thirty-minute slot. §2's per-tier item budgets stay intact.
   */
  {
    id: "settings-accessibility",
    path: "settings/accessibility",
    title: "Accessibility",
  },
  { id: "settings-camera", path: "settings/camera", title: "Camera & privacy" },
];

/**
 * 🔴 LONGEST PATH FIRST, OR `school/announcements` NEVER MATCHES.
 *
 * `school` is a prefix of `school/announcements`, and a naive first-match on a table
 * written in reading order would send every school sub-page to the dashboard.
 * Sorting by length here means the table can stay in the order a human wants to
 * read it.
 */
const BY_SPECIFICITY = [...ROUTES].sort(
  (a, b) => b.path.length - a.path.length,
);

/**
 * `#/invite/<token>` → the token, or `null` for any other hash.
 *
 * 🔴 THE TOKEN IS NEVER VALIDATED HERE, ONLY EXTRACTED. Shape-checking it in the
 * browser would create a second definition of "a valid invitation" that could
 * disagree with the server's — and the server's is a hash lookup that answers
 * one generic refusal. A client that rejected a token for looking wrong would be
 * telling the invitee something the product deliberately does not know.
 */
export function inviteTokenFrom(hash: string): string | null {
  return tokenAfter(hash, "invite/");
}

/**
 * `#/signup/<token>` → the token, or `null` for any other hash.
 *
 * ⚠️ NOT VALIDATED HERE, ONLY EXTRACTED — `inviteTokenFrom`'s rule. A client
 * that rejected a token for looking wrong would be a second definition of "a
 * valid link" that could disagree with the server's, and the server's is a hash
 * lookup answering one generic refusal.
 */
export function signupTokenFrom(hash: string): string | null {
  return tokenAfter(hash, "signup/");
}

/**
 * `#/reset/<token>` → the token, or `null` for any other hash.
 *
 * ⚠️ NOT VALIDATED HERE, ONLY EXTRACTED — `inviteTokenFrom`'s rule.
 */
export function resetTokenFrom(hash: string): string | null {
  return tokenAfter(hash, "reset/");
}

/**
 * `#/quote/<token>` → the token, or `null` for any other hash.
 *
 * ⚠️ NOT VALIDATED HERE, ONLY EXTRACTED — `inviteTokenFrom`'s rule.
 */
export function quoteTokenFrom(hash: string): string | null {
  return tokenAfter(hash, "quote/");
}

/** The shared half of the four, so they cannot drift. */
function tokenAfter(hash: string, prefix: string): string | null {
  const path = normalisePath(hash);
  if (!path.startsWith(prefix)) return null;
  const token = path.slice(prefix.length);
  return token.length > 0 ? token : null;
}

/** `#/school/announcements` → that route. Unknown hashes fall back to landing. */
export function matchRoute(hash: string): RouteDef {
  const path = normalisePath(hash);

  /*
   * ⚠️ THE ONE PREFIX MATCH, AND IT IS BEFORE THE EXACT-MATCH TABLE. Everything
   * under `invite/` is the invitation screen — the segment after it is a
   * credential, not a sub-page, so there is nothing more specific it could be.
   */
  if (path.startsWith("invite/")) {
    return ROUTES.find((r) => r.id === "invite") ?? ROUTES[0];
  }

  /*
   * ⚠️ THE SECOND PREFIX RULE. Same shape as `invite/`: the
   * segment after it is a credential rather than a sub-page, so there is nothing
   * more specific it could be. `signup/` is a top-level segment with nothing
   * guarded under it — see the route's own note for why the claim link is not at
   * `school/...` despite being a school's.
   */
  if (path.startsWith("signup/")) {
    return ROUTES.find((r) => r.id === "school-signup-claim") ?? ROUTES[0];
  }

  /*
   * ⚠️ AND TWO MORE OF THE SAME KIND: a password-reset link and a quote link.
   * Each segment after the prefix is a credential, each prefix is top-level
   * with nothing guarded under it — see the routes' own note.
   */
  if (path.startsWith("reset/")) {
    return ROUTES.find((r) => r.id === "reset-password") ?? ROUTES[0];
  }
  if (path.startsWith("quote/")) {
    return ROUTES.find((r) => r.id === "quote-accept") ?? ROUTES[0];
  }

  const hit = BY_SPECIFICITY.find((r) => r.path === path);
  /*
   * ⚠️ AN UNKNOWN HASH IS THE LANDING PAGE, NEVER A 404 SCREEN. This is a PWA a
   * school opens from a bookmark; a stale bookmark from a previous version must
   * land somewhere useful rather than on an error a teacher has to interpret.
   */
  return hit ?? ROUTES[0];
}

export function normalisePath(hash: string): string {
  return hash
    .replace(/^#/, "")
    /*
     * 🔴 THE QUERY IS NOT PART OF THE PATH, AND DROPPING IT HERE IS WHAT LETS A
     * DEEP LINK EXIST AT ALL. `#/school/students?q=Aarav` must resolve to the
     * Children screen — with the table matching on this route, it resolved to
     * nothing and fell through to the landing page.
     *
     * ⚠️ AND `canonicalHashFor` THEN LEAVES IT ALONE, which is the half that is
     * easy to miss. That function asks whether `normalisePath(canonical)` equals
     * `normalisePath(hash)`; both sides now drop the query, so the two agree and
     * it returns null rather than rewriting the address and throwing the query
     * away. A seeded link survives the router's own correction.
     */
    .replace(/[?#].*$/, "")
    .replace(/^\//, "")
    .replace(/\/+$/, "");
}

/**
 * The `?key=value` pairs on a hash, for a screen that can be deep-linked into a
 * state rather than just to an address.
 *
 * 🔴 THE CHROME'S SEARCH BOX IS WHY THIS EXISTS. Picking a child out of it has
 * to land on the Children screen *showing that child*, and this product has no
 * route parameters — `matchRoute` is an exact table with four prefix exceptions,
 * both of which are credentials. A query string adds the one thing needed
 * without adding a second way for a path to mean something.
 *
 * ⚠️ IT IS A READ, NOT A ROUTE. Nothing here decides which screen renders; a
 * screen that ignores the query renders exactly as it always did, which is what
 * makes this safe to add to a router forty-one routes already depend on.
 */
export function hashParam(hash: string, key: string): string | null {
  const q = hash.indexOf("?");
  if (q === -1) return null;
  return new URLSearchParams(hash.slice(q + 1)).get(key);
}

/**
 * The address this hash should be showing, or `null` if it already is.
 *
 * 🔴 THE ROUTER USED TO LIE ABOUT WHERE YOU WERE, AND THAT IS WHAT MADE A
 * ONE-LINE PATH RENAME LOOK LIKE A BROKEN SCREEN.
 *
 * `matchRoute` answers the landing page for anything it does not recognise —
 * deliberately, so a stale bookmark lands somewhere useful rather than on a 404
 * a teacher has to interpret. What it did NOT do was correct the address bar, so
 * the URL went on claiming `#/teacher/signin` while the marketing page rendered
 * underneath it. Every explanation for that looks identical from the outside: a
 * mis-wired link, a guard redirecting, a screen failing to mount, a stale
 * bundle. The fallback is right; being silent about it is not.
 *
 * With this, the two can never disagree: either the hash names a real route and
 * stays exactly as typed, or it is replaced with the address of the screen that
 * actually rendered. A URL is a claim about what you are looking at.
 *
 * ⚠️ IT IS `replaceState`, NEVER A NAVIGATION. Correcting an address is not a
 * place a Back press should return to.
 *
 * ⚠️ TWO HASHES ARE EXEMPT, AND BOTH WOULD BE DESTROYED BY REWRITING THEM:
 *
 *   · `#/invite/<token>` (and `signup/`, `reset/`, `quote/`) — the segment
 *     after the prefix IS the credential. Canonicalising to `#/invite` would
 *     strip the invitation out of the link.
 *   · `#how-it-works` and the marketing page's other section anchors. A route
 *     hash starts with `/` and an in-page anchor does not, which is the same
 *     line `copy.ts` draws: *"they are not routes, and the distinction is
 *     load-bearing"*. Rewriting one would fight the browser over an anchor the
 *     landing page owns.
 */
export function canonicalHashFor(hash: string): string | null {
  const raw = hash.replace(/^#/, "");

  // An in-page anchor rather than a route — the landing page's own sections.
  if (raw !== "" && !raw.startsWith("/")) return null;

  const path = normalisePath(hash);

  /* The token IS the path — canonicalising would strip the credential out of
     the link. Every one of `matchRoute`'s prefix rules is exempt for that reason. */
  if (
    path.startsWith("invite/") ||
    path.startsWith("signup/") ||
    path.startsWith("reset/") ||
    path.startsWith("quote/")
  ) {
    return null;
  }

  const canonical = `#/${matchRoute(hash).path}`;
  return normalisePath(canonical) === path ? null : canonical;
}

export function pathFor(id: RouteId): string {
  const route = ROUTES.find((r) => r.id === id);
  // A caller asking for an id that is not in the table is a bug at build time,
  // not a redirect at runtime.
  if (!route) throw new Error(`No route for "${id}"`);
  return `#/${route.path}`;
}

/**
 * The table's own name for a route — the floor under `pageTitleFor`.
 *
 * ⚠️ IT IS THE FALLBACK, NOT THE SOURCE. What a human is shown is `nav.tsx`'s
 * business (see the note on `title` above); this exists so a route added to the
 * table with no presentation line still names itself in the chrome, rather than
 * leaving the bar blank on a screen nobody thought to label.
 */
export function titleFor(id: RouteId): string {
  return ROUTES.find((r) => r.id === id)?.title ?? "";
}

/**
 * May this principal be SHOWN this screen?
 *
 * ⚠️ Again: navigation, not authorisation. A route with no `roles` is public in
 * the navigational sense only — `kid` still needs a device token and the API
 * still refuses without one.
 */
export function mayNavigate(
  route: RouteDef,
  role: Role | "DEVICE" | null,
): boolean {
  if (!route.roles) return true;
  if (role === null || role === "DEVICE") return false;
  return route.roles.includes(role);
}

/**
 * The nav items to render for a signed-in principal.
 *
 * 🔴 IT RETURNS ONLY WHAT THEY MAY OPEN. A greyed-out item for a screen a
 * principal cannot reach tells them the product has a feature they are being
 * denied, which is how a principal ends up asking an admin for a password.
 */
export function navFor(role: Role | "DEVICE" | null): RouteDef[] {
  if (role === null || role === "DEVICE") return [];
  /* ⚠️ `chrome` ROUTES ARE ROLE-GATED AND STILL NOT NAV ITEMS — they are drawn
     beside Sign out instead. See `RouteDef.chrome` and `settingsHomeFor`. */
  return ROUTES.filter(
    (r) => r.roles !== undefined && !r.chrome && mayNavigate(r, role),
  );
}

/**
 * Which screen this tier's Settings control opens.
 *
 * 🔴 ONE CONTROL, ONE WORD, AND A DESTINATION THAT DEPENDS ON WHO IS SIGNED IN —
 * OR NO CONTROL AT ALL. G1–G4 are "every signed-in adult's own settings", and
 * that was never true of the adult with no password: a parent authenticates with
 * a phone number and a one-time code, so G1's password form and its
 * authenticator panel describe an account they do not have. That tier now
 * answers `null` (ADR-051) and its preferences — the language we message them
 * in, what we may send, when not to send it, and the per-child recording switch
 * — are drawn on the parent home's Privacy & consent view
 * (`#/parent?view=privacy`; there is no `parent-privacy` route), beside the
 * export and erasure rights they belong with.
 *
 * ⚠️ IT IS A FUNCTION HERE RATHER THAN A CONDITIONAL IN THE SHELL because the
 * shell draws the control TWICE — sidebar footer on a laptop, header strip on a
 * phone — and two copies of the same per-tier test are two copies that can
 * drift. Three tiers answer `null` and one answers a screen.
 */
export function settingsHomeFor(role: Role | "DEVICE" | null): RouteId | null {
  /*
   * ✅ AND THE PARENT TIER IS THE FOURTH `null` (ADR-051), where it used to be
   * the one tier this function existed for.
   *
   * G1–G4 never fitted: §9.1 signs a parent in with a phone number and a
   * one-time code, so a password form and an authenticator panel describe an
   * account they do not have. The first answer was to point this control at a
   * parent-only settings screen; the better one is that the screen was already
   * duplicating Privacy — same tier, same family, same data — so the control has
   * gone and its contents moved there. Privacy is reached from the parent home
   * (`#/parent?view=privacy`, a sub-view rather than a route), so nothing is
   * harder to reach than it was.
   */
  if (role === "PARENT") return null;
  if (role === "ADMIN" || role === "PRINCIPAL") return null;
  /*
   * 🔴 AND THE PLATFORM TIER HAS NO SETTINGS CONTROL EITHER, WHICH IS THE THIRD
   * `null` AND THE SAME ARGUMENT AS THE SCHOOL TIER'S.
   *
   * G3 and G4 are the CLASSROOM LAPTOP's settings — reduce-motion for the
   * child's stage, and the camera-and-privacy panel describing the device a
   * four-year-old stands in front of. An operator sits at neither. They were
   * being offered a control, on every platform screen, that opened a page about
   * hardware they do not have and switches that change nothing they can see —
   * and the two things they might genuinely have wanted behind that word
   * (maintenance mode, feature flags) are the System screen, which is already a
   * sidebar item.
   *
   * ⚠️ THE SCREENS ARE NOT DELETED AND ARE STILL REACHABLE BY HASH. They carry
   * no `roles`, deliberately (see the route table), because they are per-browser
   * preferences rather than a tier's property. What has gone is the chrome that
   * pointed an operator at them.
   */
  if (role === "SUPER_ADMIN") return null;
  /*
   * ⚠️ EVERY OTHER SIGNED-IN ADULT LANDS ON G3 NOW, NOT G1. Accessibility is
   * simply the first tab they have left; `settingsTabsFor` returns G3 then G4
   * for them and this has to agree with it, or the control opens a screen whose
   * own tab strip does not contain it.
   */
  return "settings-accessibility";
}

/**
 * Is this screen the one `settingsHomeFor` opens — for the current-item state.
 *
 * ⚠️ THE `parent-settings` CLAUSE IS GONE WITH THE ROUTE (ADR-051). Privacy is
 * deliberately NOT added in its place: it is a sub-view of the parent home
 * (`#/parent?view=privacy`), not a route, and claiming it here would light up a
 * Settings control that this tier no longer draws.
 */
export function isSettingsRoute(id: RouteId): boolean {
  return id.startsWith("settings-");
}

/**
 * The screens a SIGNED-IN person has no business being on.
 *
 * 🔴 IT EXISTS FOR THE BACK BUTTON, AND THE BACK BUTTON IS WHY SESSIONS *FELT*
 * LOST EVEN AFTER THEY STOPPED BEING LOST. Restoring the credential on boot
 * fixes a reload; it does not fix this. A parent signs in at `parent/code`, is
 * `replace`d to `#/parent`, presses back — and lands on `parent/signin`, which
 * carries no `roles`, so `mayNavigate` waves it through and the app draws a
 * sign-in form at somebody who is signed in. There is no way for them to read
 * that as anything but "it logged me out".
 *
 * ⚠️ `replace` IS THE ONLY CORRECT ANSWER ONCE THIS RETURNS TRUE, and
 * `AppRouter` uses it. `navigate` would push the dashboard on top of the door,
 * so a second press of back would land on the door again — a browser that
 * refuses to go back, which is the one thing worse than the bug being fixed.
 *
 * 🔴 THE TOKEN-BEARING ROUTES ARE DELIBERATELY ABSENT. `invite`,
 * `school-signup` and `school-signup-claim` each carry a credential in the hash
 * and are a job somebody may legitimately be doing while signed in as somebody
 * else — an admin claiming an invitation for a second account, a head teacher
 * finishing a signup link. Bouncing those would make the link dead rather than
 * the screen redundant. `connect` is the public enquiry form and `kid` is play,
 * which staff open on purpose.
 *
 * ⚠️ `landing` IS IN THE LIST, which means a signed-in user cannot read the
 * marketing page without signing out. That is the trade: it is the hash the back
 * button reaches most often — it is `#/` — and a marketing page is not what
 * somebody pressing back inside a dashboard is asking for.
 */
const ENTRY_ROUTES = new Set<RouteId>([
  "landing",
  "start",
  "staff-login",
  "school-login",
  "operator-login",
  "parent-phone",
  "parent-otp",
]);

export function isEntryRoute(id: RouteId): boolean {
  return ENTRY_ROUTES.has(id);
}

/**
 * Where a principal belongs immediately after signing in.
 *
 * ⚠️ A COORDINATOR LANDS ON THE TEACHER TIER, NOT THE SCHOOL ONE. S6 scopes them
 * to assigned classes, so the school dashboard would show them a school-wide
 * figure they cannot drill into — the teacher tier is the screen their scope
 * actually fits.
 */
export function homeFor(role: Role | "DEVICE"): RouteId {
  switch (role) {
    case "DEVICE":
      return "kid";
    case "PARENT":
      return "parent";
    case "TEACHER":
    case "COORDINATOR":
      return "teacher";
    case "ADMIN":
    case "PRINCIPAL":
      return "school";
    case "SUPER_ADMIN":
      return "platform";
    default:
      /*
       * 🔴 A ROLE ADDED LATER LANDS ON THE LANDING PAGE RATHER THAN GUESSING.
       * The same fail-closed default `ScopeService` uses — a new role that
       * silently inherited a tier would be shown a dashboard nobody decided it
       * should see.
       */
      return "landing";
  }
}

/** The three staff sign-in screens. A parent's OTP flow is not a door. */
export type Door = "teacher" | "school" | "operator";

/**
 * 🔴 A DOOR IS DEFINED BY THE TIER IT OPENS, NOT BY A LIST OF ROLES — so this
 * table is three rows rather than five, and `homeFor` stays the ONE place that
 * decides which tier a role belongs to.
 *
 * The alternative, a `Record<Door, Role[]>`, is a second copy of that decision:
 * a COORDINATOR is on the teacher tier *because `homeFor` says so* (S6 scopes
 * them to assigned classes), and a role list here could be edited to disagree.
 * The failure that produces is somebody locked out of the only tier they can
 * use, which is invisible until the person it happens to phones the school.
 */
const DOOR_HOME: Record<Door, RouteId> = {
  teacher: "teacher",
  school: "school",
  operator: "platform",
};

/** Which screen each door is. Used to send a wrong-door arrival to the right one. */
export const DOOR_ROUTE: Record<Door, RouteId> = {
  teacher: "staff-login",
  school: "school-login",
  operator: "operator-login",
};

/**
 * May an account that has ALREADY authenticated stay, having arrived here?
 *
 * ⚠️ THIS IS NOT AN AUTHORISATION CHECK AND MUST NEVER BE READ AS ONE. What
 * stops a teacher opening the platform tier is `@Roles('SUPER_ADMIN')` on the
 * server and `mayNavigate` in the browser, both unchanged. This decides only
 * whether the address someone signed in at was the right one for their account.
 */
export function doorAccepts(door: Door, role: Role): boolean {
  return homeFor(role) === DOOR_HOME[door];
}

/**
 * The door this role belongs at, or `null` for a principal with no staff door
 * (PARENT, and DEVICE, which never reach a password form at all).
 *
 * 🔴 DERIVED FROM `DOOR_HOME` RATHER THAN SWITCHED ON THE ROLE, so it cannot
 * disagree with `doorAccepts` — the two answers are read off one table.
 */
export function doorFor(role: Role): Door | null {
  const home = homeFor(role);
  const hit = (Object.keys(DOOR_HOME) as Door[]).find(
    (d) => DOOR_HOME[d] === home,
  );
  return hit ?? null;
}

/**
 * What a completed sign-in turned out to be.
 *
 * 🔴 `wrong-door` IS NOT A FAILURE AND MUST NOT BE RENDERED AS ONE. The
 * credentials were correct — both factors of them. What happened is that a
 * correct account arrived at another tier's address, and the honest answer is to
 * say so and point at the right one, rather than show "that email and password
 * did not match" to somebody whose email and password matched perfectly.
 *
 * ⚠️ `door` IS `null` WHEN THE ROLE HAS NO STAFF DOOR AT ALL. `/auth/login`
 * already refuses PARENT, so nothing should reach it; the screens fall back to
 * the generic refusal rather than rendering a link to nowhere.
 *
 * ⚠️ THESE LIVE HERE, BESIDE `Door`, AND NOT IN `AppRouter`. The sign-in screens
 * consume them and `AppRouter` imports those screens, so declaring them there
 * would be a module cycle for the sake of where a reader first met the word.
 */
export type DoorOutcome =
  { at: "signed-in" } | { at: "wrong-door"; door: Door | null };

/** …plus the two states that resolve without a session existing yet. */
export type DoorSignIn = DoorOutcome | { at: "mfa" } | { at: "enrol" };

/**
 * The compulsory first enrolment's result. It carries the recovery codes, which
 * are the only copy that will ever exist — so the screen must render them before
 * anything navigates.
 */
export type EnrolmentOutcome =
  { at: "enrolled"; codes: string[] } | { at: "wrong-door"; door: Door | null };
